/**
 * The timer's audio cue: a Web Audio oscillator, no asset file (D6).
 *
 * One sound does not justify a binary in the repo plus a packaging step, and Web
 * Audio works fully offline — which is this client's default state.
 *
 * TWO things about browser audio the first version got wrong, and this one must
 * not regress:
 *
 * * A fresh `AudioContext` starts SUSPENDED until a user gesture, and the chime
 *   fires at zero — minutes after the `[Start]` click — so a context created at
 *   that point never makes a sound. The context is created and resumed once, in
 *   `primeChime`, which the timer calls from `[Start]` (a real gesture).
 * * Closing the context right after scheduling cut the audio off before the
 *   audio thread rendered it. The context is now REUSED and never closed.
 *
 * No-ops when `AudioContext` is `undefined`: happy-dom, a blocked autoplay
 * policy, or any non-browser host. The zero state is *also* announced into an
 * `aria-live="assertive"` region by the component, so the prompt never depends on
 * the sound being heard.
 */

/** The shared context, created lazily and reused for every chime. */
let context: AudioContext | null = null;

/** The shared context, created on first use. Null where Web Audio is absent. */
function audioContext(): AudioContext | null {
  if (context) return context;
  const Ctor =
    typeof AudioContext !== 'undefined'
      ? AudioContext
      : (globalThis as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  try {
    context = new Ctor();
  } catch {
    // Some hosts define the constructor but refuse to construct it.
    return null;
  }
  return context;
}

/**
 * Unlocks audio on a user gesture. The timer calls this from `[Start]`, so the
 * context exists — and is running — long before the chime is needed. A no-op
 * where Web Audio is unavailable.
 */
export function primeChime(): void {
  const ctx = audioContext();
  if (!ctx) return;
  void ctx.resume?.();
}

/**
 * R5.3: the named set of timer cues, code-only — every zero plays something,
 * so there is no mute entry here. `chime` is the original two-note prompt the
 * timer shipped with; the others are hand-authored variations on it. The
 * order is the picker's cycle order.
 */
export const TIMER_CUES = ['chime', 'bell', 'soft'] as const;
export type TimerCueId = (typeof TIMER_CUES)[number];

/** One cue's voice: a peak level, a decay, and `[offset s, frequency Hz]` notes. */
const CUES: Record<TimerCueId, { level: number; decay: number; notes: readonly (readonly [number, number])[] }> = {
  // Two notes a fifth apart, short and soft: a prompt, not an alarm.
  chime: { level: 0.15, decay: 0.16, notes: [[0, 660], [0.18, 880]] },
  // The same fifth, left to ring out.
  bell: { level: 0.15, decay: 1.4, notes: [[0, 660], [0.18, 880]] },
  // A tone lower and much quieter, for rooms with other people in them.
  soft: { level: 0.06, decay: 0.4, notes: [[0, 440], [0.22, 550]] },
};

/** The named cue. Defaults to `chime` — today's two-note prompt. */
export function playChime(cue: TimerCueId = 'chime'): void {
  const ctx = audioContext();
  if (!ctx) return;

  // A context that was never primed (or was suspended in a background tab) is
  // resumed here as a best effort; `primeChime` on `[Start]` is the reliable
  // unlock.
  void ctx.resume?.();

  try {
    const voice = CUES[cue];
    const now = ctx.currentTime;
    for (const [offset, frequency] of voice.notes) {
      const oscillator = ctx.createOscillator();
      const gain = ctx.createGain();
      oscillator.type = 'sine';
      oscillator.frequency.value = frequency;

      gain.gain.setValueAtTime(0.0001, now + offset);
      gain.gain.exponentialRampToValueAtTime(voice.level, now + offset + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + offset + voice.decay);

      oscillator.connect(gain).connect(ctx.destination);
      oscillator.start(now + offset);
      oscillator.stop(now + offset + voice.decay + 0.02);
    }
  } catch {
    // A cue is never worth an exception.
  }
}
